import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import WarningIcon from '@mui/icons-material/Warning';
import { Machine } from '../../../../ts/Connections';
import { AprilTag } from '../../../../ts/AprilTag';
import { useTranslation } from 'react-i18next';
import ForkliftTagScanner from './ForkliftTagScanner';
import { customFieldInputSx } from '../../Inputs/inputComponents/styles/CommonStyles';

interface SelectForkliftModalProps {
    open: boolean;
    setOpenForkliftModal: React.Dispatch<React.SetStateAction<boolean>>;
    forkliftList?: AprilTag[];
    setForklift?: React.Dispatch<React.SetStateAction<AprilTag | null>>;
    machine: Machine
}

const SelectForkliftModal: React.FC<SelectForkliftModalProps> = ({
    open,
    setOpenForkliftModal,
    forkliftList = [],
    setForklift,
    machine
}) => {
    const [selectedForklift, setSelectedForklift] = useState<string>('');
    const [scanning, setScanning] = useState<boolean>(false);
    const { t } = useTranslation();
    const theme = useTheme();

    const warningBackgroundColor = theme.palette.mode === 'dark' ? 'var(--spectre-warning-surface)' : theme.palette.warning.light;
    const warningBorderColor = theme.palette.mode === 'dark' ? 'var(--spectre-warning-border)' : theme.palette.warning.main;
    const warningTextColor = theme.palette.warning.dark;
    const warningHeaderTextColor = theme.palette.warning.contrastText;

    React.useEffect(() => {
        if (!open) {
            setScanning(false);
        }
    }, [open]);

    const handleChange = (event: SelectChangeEvent<string>) => {
        setSelectedForklift(event.target.value);
    };

    const handleDetected = (detectedForklift: AprilTag) => {
        setSelectedForklift(detectedForklift.tagName);
        setScanning(false);
        if (setForklift) {
            setForklift(detectedForklift);
            setOpenForkliftModal(false);
        }
    };

    const handleSave = () => {
        if (setForklift) {
            if (selectedForklift === "") {
                setForklift(null);
            } else {
                const selectedForkliftObj = forkliftList.find(f => f.tagName === selectedForklift);
                if (selectedForkliftObj) {
                    setForklift(selectedForkliftObj);
                }
            }
            setOpenForkliftModal(false);
        }
    };

    const handleClose = () => {
        setScanning(false);
        setOpenForkliftModal(false);
        setSelectedForklift('');
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('control.inputs.selectForkliftModal.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <FormControl fullWidth sx={{ flexGrow: 1, ...customFieldInputSx }}>
                        <InputLabel id="forklift-select-label">{t('control.inputs.selectForkliftModal.forkLift')}</InputLabel>
                        <Select
                            labelId="forklift-select-label"
                            id="forklift-select"
                            value={selectedForklift}
                            onChange={handleChange}
                            label="Forklift"
                        >
                            <MenuItem value="">
                                {t('control.inputs.selectForkliftModal.noForklift')}
                            </MenuItem>
                            {forkliftList.length > 0 ? (
                                forkliftList.map((name, index) => (
                                    <MenuItem key={index} value={name.tagName}>
                                        {name.tagName}
                                    </MenuItem>
                                ))
                            ) : (
                                ""
                            )}
                        </Select>
                    </FormControl>
                    <Tooltip
                        title={scanning
                            ? t('control.inputs.selectForkliftModal.scanner.stop')
                            : t('control.inputs.selectForkliftModal.scanner.scan')}
                    >
                        <IconButton
                            onClick={() => setScanning(prev => !prev)}
                            color="primary"
                            aria-label={(scanning
                                ? t('control.inputs.selectForkliftModal.scanner.stop')
                                : t('control.inputs.selectForkliftModal.scanner.scan')) ?? 'Scan with camera'}
                            sx={{
                                flexShrink: 0,
                                height: 56,
                                width: 56,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: scanning ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                            }}
                        >
                            <QrCodeScannerIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <ForkliftTagScanner
                    active={scanning}
                    forkliftList={forkliftList}
                    onDetected={handleDetected}
                />
                {
                    !machine.location ? 
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 16px',
                            backgroundColor: warningBackgroundColor,
                            backgroundImage: 'none',
                            border: `2px solid ${warningBorderColor}`,
                            borderRadius: 2,
                        }}
                    >
                        <WarningIcon sx={{ color: warningHeaderTextColor, mr: 1 }} />
                        <Typography variant="body2" sx={{ color: warningTextColor, fontWeight: 500 }}>
                            {t('control.inputs.selectForkliftModal.warning')}
                        </Typography>
                    </Paper>
                    :
                    ""
                }
            </DialogContent>
            <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
                <Button
                    onClick={handleClose}
                    className="Blue-Button Modal-Close-Button"
                    sx={{ fontWeight: 700 }}
                >
                    {t('control.inputs.selectForkliftModal.cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    color="secondary"
                    variant="contained"
                    disabled={false}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.dark,
                            color: theme.palette.secondary.contrastText,
                        },
                    }}
                >
                    {t('control.inputs.selectForkliftModal.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectForkliftModal;